import { normalizeToSimpleFeatures } from "@/tactical/io/gisNormalize";

type ShpParseResult =
  | GeoJSON.FeatureCollection
  | GeoJSON.FeatureCollection[]
  | Record<string, GeoJSON.FeatureCollection>;

function collectionsFromShp(result: ShpParseResult): GeoJSON.Feature[] {
  const list: GeoJSON.FeatureCollection[] = [];
  if (Array.isArray(result)) {
    list.push(...result);
  } else if (result && typeof result === "object") {
    if ((result as GeoJSON.FeatureCollection).type === "FeatureCollection") {
      list.push(result as GeoJSON.FeatureCollection);
    } else {
      for (const v of Object.values(result as Record<string, GeoJSON.FeatureCollection>)) {
        if (v?.type === "FeatureCollection") list.push(v);
      }
    }
  }
  return list.flatMap((fc) => fc.features ?? []);
}

async function loadShp() {
  const mod = await import("shpjs");
  return (mod.default ?? mod) as typeof import("shpjs");
}

/** ZIP shapefile ArrayBuffer → Feature[]. */
export async function parseShapefileBuffer(buf: ArrayBuffer): Promise<{
  features: GeoJSON.Feature[];
  skipped: number;
}> {
  const shp = await loadShp();
  const parsed = (await (shp.parseZip
    ? shp.parseZip(buf)
    : shp(buf))) as ShpParseResult;
  return normalizeToSimpleFeatures({
    type: "FeatureCollection",
    features: collectionsFromShp(parsed),
  });
}

/**
 * Набор файлов shapefile (.shp + .dbf + опционально .prj) или один ZIP.
 */
export async function parseShapefileFiles(files: File[]): Promise<{
  features: GeoJSON.Feature[];
  skipped: number;
}> {
  if (files.length === 1 && /\.zip$/i.test(files[0]!.name)) {
    return parseShapefileBuffer(await files[0]!.arrayBuffer());
  }

  const shp = await loadShp();
  if (!shp.parseShp || !shp.combine) {
    throw new Error("shpjs: parseShp/combine недоступны");
  }

  const byBase = new Map<string, Record<string, ArrayBuffer>>();
  for (const f of files) {
    const m = f.name.match(/^(.*)\.(shp|dbf|prj|shx|cpg)$/i);
    if (!m) continue;
    const base = m[1]!.toLowerCase();
    const ext = m[2]!.toLowerCase();
    const slot = byBase.get(base) ?? {};
    slot[ext] = await f.arrayBuffer();
    byBase.set(base, slot);
  }
  if (byBase.size === 0) {
    throw new Error("Нужен ZIP shapefile или файлы .shp/.dbf");
  }

  const all: GeoJSON.Feature[] = [];
  let skipped = 0;
  for (const parts of byBase.values()) {
    if (!parts.shp) {
      skipped += 1;
      continue;
    }
    const prjText = parts.prj ? new TextDecoder().decode(parts.prj) : undefined;
    const geometries = shp.parseShp(parts.shp, prjText);
    const dbf =
      parts.dbf && shp.parseDbf ? shp.parseDbf(parts.dbf, parts.cpg as never) : undefined;
    const fc = dbf
      ? shp.combine([geometries, dbf])
      : ({
          type: "FeatureCollection",
          features: geometries.map((geometry) => ({
            type: "Feature" as const,
            geometry,
            properties: {},
          })),
        } as GeoJSON.FeatureCollection);
    const n = normalizeToSimpleFeatures(fc);
    all.push(...n.features);
    skipped += n.skipped;
  }
  return { features: all, skipped };
}

type ShpWriteModule = {
  zip?: (
    gj: GeoJSON.FeatureCollection,
    options?: Record<string, unknown>,
  ) => Promise<unknown> | unknown;
  default?: { zip?: ShpWriteModule["zip"] };
};

export async function featureCollectionToShapefileZip(
  fc: GeoJSON.FeatureCollection,
  filenameBase: string,
): Promise<Blob> {
  const shpwrite = (await import("@mapbox/shp-write")) as ShpWriteModule;
  const zipFn = shpwrite.zip ?? shpwrite.default?.zip;
  if (typeof zipFn !== "function") throw new Error("shp-write.zip недоступен");

  const safe = (filenameBase || "board").replace(/[^\w\-]+/g, "_").slice(0, 32) || "board";
  const result = await Promise.resolve(
    zipFn(fc, {
      folder: safe,
      filename: safe,
      outputType: "blob",
      compression: "STORE",
      types: {
        point: "points",
        polygon: "polygons",
        line: "lines",
      },
    }),
  );

  if (result instanceof Blob) return result;
  if (result instanceof ArrayBuffer) {
    return new Blob([result], { type: "application/zip" });
  }
  if (typeof result === "string") {
    const bin = atob(result);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new Blob([bytes], { type: "application/zip" });
  }
  throw new Error("Неожиданный результат shp-write");
}

export function downloadBlob(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
